// src/features/static-pages/Download.jsx
// Simple public download page - no auth required
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Monitor, Terminal, Apple, CheckCircle, Copy, CheckCheck, Loader2 } from 'lucide-react';

const API_BASE = '/.netlify/functions';

const Download = () => {
  const [isLoading, setIsLoading] = useState({});
  const [downloadStarted, setDownloadStarted] = useState({});
  const [error, setError] = useState(null);
  const [copiedCommand, setCopiedCommand] = useState(false);

  const handleCopyCommand = async () => {
    await navigator.clipboard.writeText('sudo dpkg -i ContextUI-0.2.31-amd64.deb');
    setCopiedCommand(true);
    setTimeout(() => setCopiedCommand(false), 2000);
  };

  const handleDownload = async (platform) => {
    try {
      setIsLoading(prev => ({ ...prev, [platform]: true }));
      setError(null);

      // Call the public download endpoint (no auth required)
      const response = await fetch(`${API_BASE}/public-download?platform=${platform}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Download not available');
      }

      if (!data.downloadUrl) {
        throw new Error('Failed to generate download link');
      }

      window.location.href = data.downloadUrl;
      setDownloadStarted(prev => ({ ...prev, [platform]: true }));

    } catch (err) {
      console.error('Download error:', err);
      setError(err.message);
    } finally {
      setIsLoading(prev => ({ ...prev, [platform]: false }));
    }
  };

  return (
    <>
      <Helmet>
        <title>Download ContextUI - Free Desktop App</title>
        <meta name="description" content="Download ContextUI for Windows, Linux, or Mac. Build and run AI-powered workflows locally." />
      </Helmet>

      <div className="min-h-screen bg-black text-white pt-20 px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4">Download ContextUI</h1>
            <p className="text-xl text-gray-400">
              Free desktop app for Windows, Linux, and Mac
            </p>
          </div>

          {error && (
            <div className="mb-8 p-4 bg-red-900/30 border border-red-600 rounded-lg text-red-400 text-center">
              {error}
            </div>
          )}

          {/* Download Cards */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {/* Windows */}
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-blue-600/20 rounded-lg">
                  <Monitor className="h-6 w-6 text-blue-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Windows</h3>
                  <p className="text-sm text-gray-500">v0.2.30 • 64-bit</p>
                </div>
              </div>
              <button
                onClick={() => handleDownload('Windows')}
                disabled={isLoading.Windows}
                className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                {isLoading.Windows ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : downloadStarted.Windows ? (
                  <>
                    <CheckCircle className="h-5 w-5" />
                    Download Started
                  </>
                ) : (
                  'Download for Windows'
                )}
              </button>
            </div>

            {/* Linux */}
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-orange-600/20 rounded-lg">
                  <Terminal className="h-6 w-6 text-orange-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Linux</h3>
                  <p className="text-sm text-gray-500">v0.2.31 • Debian/Ubuntu</p>
                </div>
              </div>
              <button
                onClick={() => handleDownload('Linux')}
                disabled={isLoading.Linux}
                className="w-full py-3 px-4 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                {isLoading.Linux ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : downloadStarted.Linux ? (
                  <>
                    <CheckCircle className="h-5 w-5" />
                    Download Started
                  </>
                ) : (
                  'Download for Linux'
                )}
              </button>
              
              {/* Linux install command */}
              <div className="mt-4 p-3 bg-gray-800 rounded-lg">
                <p className="text-xs text-gray-500 mb-2">Install command:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm text-gray-300 font-mono">
                    sudo dpkg -i ContextUI-0.2.31-amd64.deb
                  </code>
                  <button
                    onClick={handleCopyCommand}
                    className="p-1.5 hover:bg-gray-700 rounded transition-colors"
                    title="Copy command"
                  >
                    {copiedCommand ? (
                      <CheckCheck className="h-4 w-4 text-green-400" />
                    ) : (
                      <Copy className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Mac ARM */}
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-gray-600/20 rounded-lg">
                  <Apple className="h-6 w-6 text-gray-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Mac (Apple Silicon)</h3>
                  <p className="text-sm text-gray-500">v0.2.30 • M1/M2/M3</p>
                </div>
              </div>
              <button
                onClick={() => handleDownload('Mac_ARM')}
                disabled={isLoading.Mac_ARM}
                className="w-full py-3 px-4 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                {isLoading.Mac_ARM ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : downloadStarted.Mac_ARM ? (
                  <>
                    <CheckCircle className="h-5 w-5" />
                    Download Started
                  </>
                ) : (
                  'Download for Mac (ARM)'
                )}
              </button>
            </div>

            {/* Mac Intel */}
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-gray-600/20 rounded-lg">
                  <Apple className="h-6 w-6 text-gray-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Mac (Intel)</h3>
                  <p className="text-sm text-gray-500">v0.2.30 • x64</p>
                </div>
              </div>
              <button
                onClick={() => handleDownload('Mac_Intel')}
                disabled={isLoading.Mac_Intel}
                className="w-full py-3 px-4 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                {isLoading.Mac_Intel ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : downloadStarted.Mac_Intel ? (
                  <>
                    <CheckCircle className="h-5 w-5" />
                    Download Started
                  </>
                ) : (
                  'Download for Mac (Intel)'
                )}
              </button>
            </div>
          </div>

          {/* Info Section */}
          <div className="text-center text-gray-500 text-sm space-y-2">
            <p>
              ContextUI is free to download and use. Create an account to access the{' '}
              <a href="/exchange" className="text-blue-400 hover:underline">Exchange</a>{' '}
              and share workflows.
            </p>
            <p>
              Need help? Check out the{' '}
              <a href="/docs" className="text-blue-400 hover:underline">documentation</a>{' '}
              or{' '}
              <a href="/support" className="text-blue-400 hover:underline">contact support</a>.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Download;
